import { flextree } from "d3-flextree";
import { Edge } from "@xyflow/react";
import { RFNodeType } from "../components/GraphBuilder/Nodes/utils/types";
import type { NormalizedGraph } from "./normalizedHelpers";
import {
  denormalizeNodes,
  denormalizeEdges,
  normalizeNodes,
  normalizeEdges,
} from "./normalizedHelpers";

const DEFAULT_NODE_WIDTH = 350;
const DEFAULT_NODE_HEIGHT = 300;
const H_GAP = 130;
const V_GAP = 60;
const LEAF_NODE_EDGE_HEIGHT = 50;

export type NodeDimensionsMap = Record<
  string,
  { width: number; height: number }
>;

interface TreeNode {
  id: string;
  width: number;
  height: number;
  children: TreeNode[];
}

const buildTree = (
  nodes: RFNodeType[],
  nodeDimensions?: NodeDimensionsMap
): { roots: TreeNode[]; orphanIds: Set<string> } => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();

  const buildNode = (rfNode: RFNodeType): TreeNode => {
    visited.add(rfNode.id);
    const dims = nodeDimensions?.[rfNode.id];
    const childIds = rfNode.data.childNodeIds ?? [];
    const children: TreeNode[] = [];

    for (const childId of childIds) {
      const childRF = nodeMap.get(childId);
      if (childRF && !visited.has(childId)) {
        children.push(buildNode(childRF));
      }
    }

    return {
      id: rfNode.id,
      width: dims?.width ?? DEFAULT_NODE_WIDTH,
      height: dims?.height ?? DEFAULT_NODE_HEIGHT,
      children,
    };
  };

  const roots: TreeNode[] = [];
  for (const node of nodes) {
    if (!node.data.parentNodeId) {
      roots.push(buildNode(node));
    }
  }

  const orphanIds = new Set<string>();
  for (const node of nodes) {
    if (!visited.has(node.id)) {
      orphanIds.add(node.id);
    }
  }

  return { roots, orphanIds };
};

const layoutTree = (
  root: TreeNode,
  positionMap: Map<string, { x: number; y: number }>,
  offsetX: number,
  anchorToRootCenter: boolean
) => {
  const tree = flextree<TreeNode>({
    children: (d) => (d.children.length > 0 ? d.children : undefined),
    nodeSize: (node) => [
      node.data.width + H_GAP,
      node.data.height + V_GAP + LEAF_NODE_EDGE_HEIGHT,
    ],
    spacing: 0,
  });

  const hierarchy = tree.hierarchy(root);
  const laid = tree(hierarchy);

  let minX = Infinity;
  laid.each((node) => {
    const left = node.x - node.data.width / 2;
    if (left < minX) minX = left;
  });

  // Anchor the primary tree on its root's center instead of its left edge.
  // With left-edge anchoring (minX), any width change deep inside a branch
  // (node added/deleted, real dimensions measured, BE-synced branches)
  // changes minX and horizontally translates the ENTIRE tree, so the whole
  // canvas visibly jumps. Anchoring on the root keeps the trigger/spine
  // stationary; only the branches that actually changed move.
  // Secondary roots (defensive; journeys have a single trigger root) keep
  // left-edge anchoring so horizontal stacking cannot overlap.
  const anchorX = anchorToRootCenter ? laid.x : minX;

  let maxRight = 0;
  laid.each((node) => {
    positionMap.set(node.data.id, {
      x: node.x - node.data.width / 2 - anchorX + offsetX,
      y: node.y,
    });
    const right = node.x + node.data.width / 2 - anchorX + offsetX;
    if (right > maxRight) maxRight = right;
  });

  return maxRight;
};

export const getLayoutedElements = (
  nodes: RFNodeType[],
  edges: Edge[],
  options: {
    direction?: "TB" | "LR";
    nodeDimensions?: NodeDimensionsMap;
  } = {}
): { nodes: RFNodeType[]; edges: Edge[] } => {
  if (nodes.length === 0) return { nodes, edges };

  const { nodeDimensions } = options;
  const { roots, orphanIds } = buildTree(nodes, nodeDimensions);

  const positionMap = new Map<string, { x: number; y: number }>();

  let offsetX = 0;
  for (const [rootIndex, root] of roots.entries()) {
    const rightEdge = layoutTree(root, positionMap, offsetX, rootIndex === 0);
    offsetX = rightEdge + H_GAP * 2;
  }

  // Place orphaned nodes (not reachable from any root) in a row below
  let orphanX = 0;
  let maxY = 0;
  positionMap.forEach((pos) => {
    if (pos.y > maxY) maxY = pos.y;
  });

  for (const id of orphanIds) {
    const dims = nodeDimensions?.[id];
    const w = dims?.width ?? DEFAULT_NODE_WIDTH;
    const h = dims?.height ?? DEFAULT_NODE_HEIGHT;
    positionMap.set(id, {
      x: orphanX,
      y: maxY + h + V_GAP * 2,
    });
    orphanX += w + H_GAP;
  }

  const layoutedNodes = nodes.map((node) => {
    const pos = positionMap.get(node.id);
    return {
      ...node,
      position: pos ?? { x: 0, y: 0 },
    };
  });

  return { nodes: layoutedNodes, edges };
};

/**
 * Wrapper around getLayoutedElements that accepts and returns NormalizedGraph.
 * Internally denormalizes for d3-flextree, then renormalizes the positioned result.
 */
export const getLayoutedNormalized = (
  graph: NormalizedGraph,
  options: {
    direction?: "TB" | "LR";
    nodeDimensions?: NodeDimensionsMap;
  } = {}
): NormalizedGraph => {
  const nodes = denormalizeNodes(graph.nodesById, graph.nodeIds);
  const edges = denormalizeEdges(graph.edgesById, graph.edgeIds);

  const result = getLayoutedElements(nodes, edges, options);

  return {
    ...normalizeNodes(result.nodes),
    ...normalizeEdges(result.edges),
  };
};
